import { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";
import { cn } from "@/lib/utils";
import { Ago, SeverityIcon, StatePill } from "../../components/primitives";
import { formatClock, formatCount, formatDuration, formatMs, formatMsTick, formatPercent } from "../../format";
import { STATUS } from "../../palette";
import type { PodPhases, PodSnapshot, PoolSnapshot } from "../../types";
import { LiveChart, type LiveRow, type LiveSeries } from "../backend/live-chart";
import { FactList, SeriesSwatch, StatTile, SubHeading } from "../backend/stat-tile";
import { failureTone, isRecent, LIMITS, quantile, type Tone } from "../backend/tone";
import { podColor } from "./pod-order";
import { PodSide } from "./pod-side";

/** phases of one request in time order (pizza-backend vision/pod_pool.py _timing_trace_config) */
const PHASES = ["lock", "conn", "upload", "pod", "read"] as const;
type Phase = (typeof PHASES)[number];

/** an ordinal grey ramp in time order; only a NEW connection (warm socket lost) is coloured */
const PHASE_COLORS: Record<Phase, string> = {
  lock: "#4a4944",
  conn: "#5f5e59",
  upload: "#77766f",
  pod: "#93928b",
  read: "#b6b5ae",
};

const phaseMs = (phases: PodPhases, phase: Phase): number => Math.max(0, phases[`${phase}_ms`] ?? 0);

/**
 * Everything known about one pod: health, counters, the last request's phases and the latest requests,
 * then the pod's own view (GPU, models, pod-side latency, errors).
 */
export function PodCard({
  pod,
  index,
  pool,
  now,
}: {
  pod: PodSnapshot;
  index: number;
  pool: PoolSnapshot | null;
  /** time of the live snapshot: "recent" is judged against it, not a ticking clock */
  now: number;
}) {
  const { t } = useLocalization();
  const color = podColor(index);
  const finished = pod.success + pod.failure;
  const rate = finished ? pod.failure / finished : null;
  const rateTone = failureTone(pod.failure, finished);
  const health: Tone = !pod.healthy ? "nok" : pod.consecutive_failures > 0 ? "warn" : "ok";
  const consecutiveTone: Tone =
    pod.consecutive_failures >= LIMITS.unhealthyAfterFailures ? "nok" : pod.consecutive_failures > 0 ? "warn" : "neutral";
  const errorCurrent = !!pod.last_error && (!pod.healthy || isRecent(pod.last_error_at, now));
  const pingTone: Tone = pod.last_ping_ready === false ? "warn" : "neutral";

  let keeper: { text: string; tone: Tone };
  if (!pool?.keeper_enabled) keeper = { text: t("monitoring.pods.keeper.disabled"), tone: "neutral" };
  else if (pod.keeper_404) keeper = { text: t("monitoring.pods.keeper.missingDrain"), tone: "warn" };
  else if (pod.keeper_running) keeper = { text: t("monitoring.pods.keeper.running"), tone: "neutral" };
  else keeper = { text: t("monitoring.pods.keeper.stopped"), tone: pool.session_open ? "warn" : "neutral" };

  return (
    <Card className={cn("flex min-w-0 flex-col overflow-hidden", !pod.healthy && "border-red-500/40")}>
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2 border-b px-4 py-3">
        <SeriesSwatch color={color} className="mt-1" />
        <div className="min-w-0 flex-1">
          <div className="break-all font-mono text-sm font-semibold leading-tight">{pod.endpoint}</div>
          <div className="mt-0.5 flex flex-wrap gap-x-2 text-xs text-muted-foreground">
            <span className="break-all">{pod.locality || t("monitoring.pods.locality.none")}</span>
            <span aria-hidden>·</span>
            {pod.health_changed_at ? (
              <span>
                {t("monitoring.pods.health.changed")} <Ago ts={pod.health_changed_at} />
              </span>
            ) : (
              <span>{t("monitoring.pods.health.unchanged")}</span>
            )}
          </div>
        </div>
        <StatePill tone={health} pulse={!pod.healthy}>
          {!pod.healthy
            ? t("monitoring.pods.health.unhealthy")
            : pod.consecutive_failures > 0
              ? t("monitoring.pods.health.failing")
              : t("monitoring.pods.health.healthy")}
        </StatePill>
      </div>

      <div className="flex flex-col gap-4 p-4">
        {(pod.keeper_404 || pod.last_ping_ready === false) && (
          <div className="flex flex-col gap-1.5 text-xs">
            {pod.last_ping_ready === false && (
              <div className="flex items-start gap-2 text-amber-300">
                <SeverityIcon severity="warning" className="mt-px" />
                <span className="min-w-0 break-words">{t("monitoring.pods.ping.notReadyDetail")}</span>
              </div>
            )}
            {pod.keeper_404 && (
              <div className="flex items-start gap-2 text-amber-300">
                <SeverityIcon severity="warning" className="mt-px" />
                <span className="min-w-0 break-words">{t("monitoring.pods.keeper.404Detail")}</span>
              </div>
            )}
          </div>
        )}

        <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 8.5rem), 1fr))" }}>
          <StatTile
            size="sm"
            label={t("monitoring.pods.stat.ping")}
            tone={pingTone}
            hint={t("monitoring.pods.stat.pingHint")}
            value={pod.last_ping_ms !== null ? formatMs(pod.last_ping_ms) : "–"}
            sub={
              pod.last_ping_at ? (
                <>
                  {pod.last_ping_ready === false ? `${t("monitoring.pods.ping.notReady")} · ` : ""}
                  <Ago ts={pod.last_ping_at} />
                </>
              ) : (
                t("monitoring.pods.ping.never")
              )
            }
          />
          <StatTile
            size="sm"
            label={t("monitoring.pods.stat.ewma")}
            hint={t("monitoring.pods.stat.ewmaHint")}
            tone={pod.ewma_latency_ms > LIMITS.podSlowMs ? "warn" : "neutral"}
            value={pod.ewma_latency_ms > 0 ? formatMs(pod.ewma_latency_ms) : "–"}
            sub={t("monitoring.pods.stat.ewmaSub")}
          />
          <StatTile
            size="sm"
            label={t("monitoring.pods.stat.inFlight")}
            value={formatCount(pod.in_flight)}
            sub={t("monitoring.pods.stat.inFlightSub")}
          />
          <StatTile
            size="sm"
            label={t("monitoring.pods.stat.requests")}
            value={formatCount(pod.total)}
            sub={t("monitoring.pods.stat.requestsSub", {
              ok: formatCount(pod.success),
              fail: formatCount(pod.failure - pod.timeout),
              timeout: formatCount(pod.timeout),
            })}
          />
          <StatTile
            size="sm"
            label={t("monitoring.pods.stat.failureRate")}
            tone={rateTone}
            value={rate === null ? "–" : formatPercent(rate, rate > 0 && rate < 0.1 ? 1 : 0)}
            sub={t("monitoring.pods.stat.sinceStart")}
          />
          <StatTile
            size="sm"
            label={t("monitoring.pods.stat.consecutive")}
            tone={consecutiveTone}
            value={formatCount(pod.consecutive_failures)}
            sub={t("monitoring.pods.stat.consecutiveSub", { limit: LIMITS.unhealthyAfterFailures })}
          />
        </div>

        <FactList
          items={[
            {
              label: t("monitoring.pods.stat.lastSuccess"),
              value: <Ago ts={pod.last_success_at} fallback={t("monitoring.pods.never")} />,
            },
            {
              label: t("monitoring.pods.stat.lastFailure"),
              tone: isRecent(pod.last_failure_at, now) ? "warn" : undefined,
              value: <Ago ts={pod.last_failure_at} fallback={t("monitoring.pods.never")} />,
            },
            {
              label: t("monitoring.pods.stat.deactivations"),
              tone: pod.deactivations > 0 ? "warn" : undefined,
              value: formatCount(pod.deactivations),
              hint: t("monitoring.pods.stat.deactivationsHint"),
            },
            {
              label: t("monitoring.pods.stat.idle"),
              value: pod.idle_s === null ? t("monitoring.pods.stat.idleNever") : formatDuration(pod.idle_s),
              hint: t("monitoring.pods.stat.idleHint"),
            },
            {
              label: t("monitoring.pods.keeper.label"),
              tone: keeper.tone,
              value: keeper.text,
              hint: t("monitoring.pods.tile.keeperHint"),
            },
          ]}
        />

        {pod.last_error && (
          <div
            className={cn(
              "rounded-md border px-3 py-2 text-xs",
              errorCurrent ? "border-red-500/30 bg-red-500/10 text-red-300" : "border-border/70 text-muted-foreground",
            )}
          >
            <div className="mb-1 flex flex-wrap items-center gap-x-2">
              {errorCurrent && <SeverityIcon severity="critical" className="h-3.5 w-3.5" />}
              <span className="font-medium">{t("monitoring.pods.lastError")}</span>
              <Ago ts={pod.last_error_at} className="text-[11px] opacity-80" />
            </div>
            <p className="break-words font-mono text-[11px] leading-snug">{pod.last_error}</p>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <SubHeading
            actions={
              pod.last_phases?.at ? <Ago ts={pod.last_phases.at} className="text-[11px] text-muted-foreground" /> : undefined
            }
          >
            {t("monitoring.pods.phases.title")}
          </SubHeading>
          {pod.last_phases ? (
            <PhaseBar phases={pod.last_phases} />
          ) : (
            <p className="text-xs text-muted-foreground">{t("monitoring.pods.phases.none")}</p>
          )}
        </div>

        <RecentRequests pod={pod} color={color} />

        <PodSide pod={pod} color={color} now={now} />
      </div>
    </Card>
  );
}

/** stacked horizontal bar of the last successful request's phases, with a legend carrying the values */
function PhaseBar({ phases }: { phases: PodPhases }) {
  const { t } = useLocalization();
  const parts = PHASES.map((phase) => ({ phase, ms: phaseMs(phases, phase) }));
  const sum = parts.reduce((total, part) => total + part.ms, 0);
  const base = Math.max(sum, phases.total_ms, 1);
  const colorOf = (phase: Phase) => (phase === "conn" && !phases.conn_reused ? STATUS.warning : PHASE_COLORS[phase]);
  const label = (phase: Phase) =>
    phase === "conn"
      ? `${t("monitoring.pods.phase.conn")} ${phases.conn_reused ? t("monitoring.pods.phase.reused") : t("monitoring.pods.phase.new")}`
      : t(`monitoring.pods.phase.${phase}` as TranslationKey);

  return (
    <div className="flex flex-col gap-2">
      <div
        className="flex h-3 w-full gap-[2px] overflow-hidden rounded-sm bg-muted/40"
        role="img"
        aria-label={parts.map((part) => `${label(part.phase)} ${formatMs(part.ms)}`).join(", ")}
      >
        {parts
          .filter((part) => part.ms >= 0.5)
          .map((part) => (
            <div
              key={part.phase}
              className="h-full"
              style={{ flex: `${part.ms} 1 0px`, minWidth: 2, backgroundColor: colorOf(part.phase) }}
              title={`${label(part.phase)}: ${formatMs(part.ms)}`}
            />
          ))}
        {/* time outside the measured phases (response parsing) stays the empty track */}
        {base - sum >= 0.5 && <div className="h-full" style={{ flex: `${base - sum} 1 0px` }} aria-hidden />}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px]">
        {parts.map((part) => (
          <span
            key={part.phase}
            className="inline-flex items-center gap-1.5"
            title={t(`monitoring.pods.phase.${part.phase}Hint` as TranslationKey)}
          >
            <SeriesSwatch color={colorOf(part.phase)} className="h-2 w-2" />
            <span className={cn("text-muted-foreground", part.phase === "conn" && !phases.conn_reused && "text-amber-300")}>
              {label(part.phase)}
            </span>
            <span className="font-mono tabular-nums text-foreground">{formatMs(part.ms)}</span>
          </span>
        ))}
      </div>
      {!phases.conn_reused && (
        <div className="flex items-start gap-2 text-xs text-amber-300">
          <SeverityIcon severity="warning" className="mt-px h-3.5 w-3.5" />
          <span className="min-w-0 break-words">{t("monitoring.pods.phase.newWarning")}</span>
        </div>
      )}
      <div className="break-words text-[11px] text-muted-foreground">
        <span className="font-mono">{phases.path}</span>
        {" · "}
        <span className="tabular-nums">
          {t("monitoring.pods.phases.meta", {
            total: formatMs(phases.total_ms),
            in: formatCount(phases.in_kb),
            out: formatCount(phases.out_kb),
          })}
        </span>
      </div>
    </div>
  );
}

/** the pod's ring of the latest requests (up to 240): latency line, failed attempts as red dots */
function RecentRequests({ pod, color }: { pod: PodSnapshot; color: string }) {
  const { t, tp } = useLocalization();
  const recent = useMemo(() => pod.recent ?? [], [pod.recent]);

  const { rows, sorted, failures } = useMemo(() => {
    const okValues = recent.filter((r) => r[2] && typeof r[1] === "number").map((r) => r[1] as number);
    const sortedOk = [...okValues].sort((a, b) => a - b);
    const top = sortedOk.length ? sortedOk[sortedOk.length - 1] : 0;
    return {
      rows: recent.map(([ts, ms, ok]): LiveRow => ({ ts, ok: ok ? ms : null, fail: ok ? null : (ms ?? top) })),
      sorted: sortedOk,
      failures: recent.filter((r) => !r[2]).length,
    };
  }, [recent]);

  const series = useMemo<LiveSeries[]>(
    () => [
      { key: "ok", label: t("monitoring.pods.recent.latency"), color },
      ...(failures ? [{ key: "fail", label: t("monitoring.pods.recent.failed"), color: STATUS.critical, dots: true }] : []),
    ],
    [t, color, failures],
  );

  return (
    <div className="flex flex-col gap-2">
      <SubHeading>{t("monitoring.pods.recent.title")}</SubHeading>
      {recent.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t("monitoring.pods.recent.empty")}</p>
      ) : (
        <>
          <p className="break-words text-[11px] tabular-nums text-muted-foreground">
            {t("monitoring.pods.recent.summary", {
              n: recent.length,
              time: formatClock(recent[0][0], false),
              p50: formatMs(quantile(sorted, 0.5)),
              p95: formatMs(quantile(sorted, 0.95)),
              max: formatMs(sorted.length ? sorted[sorted.length - 1] : null),
            })}
            {failures > 0 && (
              <span className="text-red-300"> · {tp("monitoring.pods.recent.failures", failures)}</span>
            )}
          </p>
          <LiveChart
            key={series.length}
            rows={rows}
            series={series}
            height={120}
            format={formatMs}
            tickFormat={formatMsTick}
            emptyText={t("monitoring.pods.recent.empty")}
          />
        </>
      )}
    </div>
  );
}
